import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isAuthenticated, getUser } from '../utils/auth';
import Swal from 'sweetalert2';

const UserManagementForm = () => {
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    ic_no: '',
    password: '',
    role: 'employee',
    company: '',
    department: '',
    designation: '',
    employeeNo: '',
    contactNo: '',
    emergencyContactNo: '',
    relationship: '',
    status: 'active'
  });
  const navigate = useNavigate();

  // Base API URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const loggedInUser = getUser();
  console.log ("loggedinuser ", loggedInUser);

  // Fetch roles on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/users/get_roles.php`, { credentials: 'include' });
        const data = await response.json();
        if (Array.isArray(data)) {
          setRoles(data);
        } else {
          throw new Error("Invalid roles data format");
        }
      } catch (error) {
        setError(error.message);
       }
    };
    fetchRoles();
  }, []);

  // Fetch companies on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/companies/get_companies.php`);
        const data = await response.json();
        if (Array.isArray(data)) {
          setCompanies(data);
        } else {
          throw new Error("Invalid companies data format");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  // Fetch departments when the company is selected
  /*
  useEffect(() => {
    if (formData.company) {
      const fetchDepartments = async () => {
        setLoadingDepartments(true);
        try {
          const response = await fetch(`${API_BASE_URL}/departments/get_departments.php?company_id=${formData.company}`);
          const data = await response.json();
          if (Array.isArray(data)) {
            setDepartments(data);
          } else {
            throw new Error("Invalid departments data format");
          }
        } catch (error) {
          setError(error.message);
        } finally {
          setLoadingDepartments(false);
        }
      };
      fetchDepartments();
    } else {
      setDepartments([]);
    }
  }, [formData.company]);
  */

  // Fetch departments when the component mounts if a company is already assigned OR when company changes
  /*
  useEffect(() => {
    const fetchDepartments = async (companyId) => {
      setLoadingDepartments(true);
      try {
        const response = await fetch(`${API_BASE_URL}/departments/get_departments.php?company_id=${companyId}`);
        const data = await response.json();
        if (Array.isArray(data)) {
          setDepartments(data);
        } else {
          throw new Error("Invalid departments data format");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoadingDepartments(false);
      }
    };

    if (loggedInUser.role !== 'masteradmin') {
      setFormData((prevData) => ({
        ...prevData,
        company: loggedInUser.company_id,
      }));
      fetchDepartments(loggedInUser.company_id);
    } else if (formData.company) {
      fetchDepartments(formData.company);
    }
  }, [formData.company, loggedInUser.role]);
  */

  useEffect(() => {
    const fetchDepartments = async (companyId) => {
      setLoadingDepartments(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/departments/get_departments.php?company_id=${companyId}`
        );
        const data = await response.json();
        if (Array.isArray(data)) {
          setDepartments(data);

          // Auto-set department based on user role or available departments
          if (loggedInUser.role !== 'masteradmin') {
            let departmentToSet = '';
            // Check if user has a specific department assigned
            if (loggedInUser.department_id) {
              departmentToSet = loggedInUser.department_id.toString();
            } 
            // If no specific department but only one exists, set it
            else if (data.length === 1) {
              departmentToSet = data[0].id.toString();
            }

            if (departmentToSet) {
              setFormData((prev) => ({
                ...prev,
                department: departmentToSet,
              }));
            }
          }
        } else {
          throw new Error("Invalid departments data format");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoadingDepartments(false);
      }
    };

    if (loggedInUser.role !== 'masteradmin') {
      setFormData((prevData) => ({
        ...prevData,
        company: loggedInUser.company_id,
      }));
      fetchDepartments(loggedInUser.company_id);
    } else if (formData.company) {
      fetchDepartments(formData.company);
    }
  }, [formData.company, loggedInUser.role, loggedInUser.company_id, loggedInUser.department_id]);

  // Handle input changes and update formData state
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission using FormData
  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataObj = new FormData();

    // Append all form fields to FormData
    Object.keys(formData).forEach(key => {
      formDataObj.append(key, formData[key]);
    });

    // Instead of submitting via axios, log the formData to console
    for (const pair of formDataObj.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }
    fetch(`${API_BASE_URL}/users/save_user.php`, {
      method: 'POST',
      credentials: 'include',
      body: formDataObj,
    })
    .then(async response => {
      const contentType = response.headers.get('content-type');
  
      // Log the status and response text for debugging
      console.log('Response Status:', response.status);
      
      // Attempt to read the response as text first
      return response.text().then((text) => {
        console.log('Raw Response:', text); // Log the raw response text
        
        if (!response.ok) {
          // If response is not ok, throw an error with the response text
          throw new Error(`Failed with status ${response.status}: ${text}`);
        }
  
        // If response is JSON, parse it
        if (contentType && contentType.includes('application/json')) {
          return JSON.parse(text); // Parse JSON manually
        } else {
          // Return the raw text if it's not JSON
          return text;
        }
      });
    })
    .then(data => {
      console.log("data",data);
      // Check if the response is JSON or plain text
      if (typeof data === 'string') {
        console.log('Non-JSON response:', data);
        Swal.fire({ icon: 'error', text: `Received non-JSON response: ${data}` });
      } else if (data.success) {
        navigate('/users');
      } else {
        console.error('PHP Error:', data.error); // Log PHP error to the console
        Swal.fire({ icon: 'error', text: `Failed to save user: ${data.error}` });
      }
    })
    .catch(error => {
      console.error('Error saving user:', error);
      Swal.fire({ icon: 'error', text: `Error: ${error.message}` });
    });

    // Further actions (e.g., sending the formDataObj) can be handled here.
  };

  if (loadingCompanies) {
    return <div>Loading Companies...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="container mt-4">
      <h2>Create User</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Name</label>
          <input
            type="text"
            className="form-control"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-control"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">IC/Passport No.</label>
          <input
            type="text"
            className="form-control"
            name="ic_no"
            value={formData.ic_no}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Password</label>
          <input
            type="password"
            className="form-control"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Role</label>
          <select
            className="form-select"
            name="role"
            value={formData.role}
            onChange={handleChange}
            required
          >
            <option value="" hidden>Select a role</option>
            {roles
            // Filter roles based on the logged-in user's role
            .filter((role) => {
              if (loggedInUser.role === 'trainingadmin') {
                // Only allow 'trainingadmin' and 'trainer' for training admins
                return role.name === 'trainingadmin' || role.name === 'trainer';
              }
              else if (loggedInUser.role === 'masteradmin') {
                return role.name !== 'admin';
              }
              else if (loggedInUser.role === 'companyadmin') {
                return role.name === 'companyadmin' || role.name === 'departmentadmin' || role.name === 'employee';
              }
              else if (loggedInUser.role === 'departmentadmin') {
                return role.name === 'departmentadmin' || role.name === 'employee';
              }
              // For other users, show all roles (you can customize this as needed)
              return true;
            }).map((role) => (
              <option key={role.name} value={role.name}>
                {role.label}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">
            {(formData.role === 'trainingadmin' || formData.role === 'trainer') ? 'Training Vendor' : 'Company/Subsidiary'}
          </label>
          <select
            className="form-select"
            name="company"
            value={formData.company}
            onChange={handleChange}
            required
            disabled={loggedInUser.role !== 'masteradmin'}
            style={{ pointerEvents: loggedInUser.role !== 'masteradmin' ? 'none' : 'auto' }}
          >

            {loggedInUser.role === 'masteradmin' && (
               <option value="" hidden>Select a company</option>
            )}
            {companies
            // A vendor-side role (trainingadmin/trainer) only ever sees external
            // (vendor) companies; every other role only sees internal ones — a
            // user can never be tied to both at once, since it's one field now.
            .filter((company) => {
              const wantsExternal = formData.role === 'trainingadmin' || formData.role === 'trainer';
              if (loggedInUser.role === 'masteradmin') {
                return wantsExternal ? company.company_type === 'external' : company.company_type === 'internal';
              }
              else {
                return company.id === loggedInUser.company_id;
              }

            }).map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}


          </select>


        </div>

        <div className="mb-3">
          <label className="form-label">Department</label>

          <select
            className="form-select"
            name="department"
            value={formData.department}
            onChange={handleChange}
            required={formData.role !== 'trainingadmin' && formData.role !== 'trainer'}
            disabled={loggedInUser.role === 'departmentadmin' || loggedInUser.role === 'employee'}
            style={{ pointerEvents: loggedInUser.role === 'departmentadmin' || loggedInUser.role === 'employee' ? 'none' : 'auto' }}
          >
            {loggedInUser.role !== 'departmentadmin' && loggedInUser.role !== 'employee' && (
               <option value="" hidden>Select a department</option>
            )}
            {loadingDepartments ? (
              <option>Please choose Company/Subsidiary</option>
            ) : (
              (loggedInUser.role === 'departmentadmin' || loggedInUser.role === 'employee')
                ? departments
                    .filter(department => department.id === loggedInUser.department_id)
                    .map(department => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))
                : departments.map(department => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))
            )}
          </select>

        </div>

        <div className="mb-3">
          <label className="form-label">Designation</label>
          <input
            type="text"
            className="form-control"
            name="designation"
            value={formData.designation}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Employee No.</label>
          <input
            type="text"
            className="form-control"
            name="employeeNo"
            value={formData.employeeNo}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Contact No.</label>
          <input
            type="text"
            className="form-control"
            name="contactNo"
            value={formData.contactNo}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Emergency Contact No.</label>
          <input
            type="text"
            className="form-control"
            name="emergencyContactNo"
            value={formData.emergencyContactNo}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Relationship</label>
          <input
            type="text"
            className="form-control"
            name="relationship"
            value={formData.relationship}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <button type="submit" className="btn btn-primary">Create User</button>
      </form>
    </div>
  );
};

export default UserManagementForm;
